import { create } from "zustand";
import { persist } from "zustand/middleware";
import { StorageKey } from "../../appSettings";

interface TableSettings {
  /** Whether the filters is toggled on or off */
  filterToggled: boolean | undefined;
  /** The searched text */
  searchedText: string | undefined;
  /** The selected filters */
  searchedFilters: string[] | undefined;
  /** Unique identifier for the search */
  searchedId: string | undefined;
}

/**
 * Stores the table search state. Key uses tableOption, entityId, customerGroupId and apiUrl.
 */
interface TableSearchState {
  /** A record of table settings by table key */
  tableSettings: Record<string, TableSettings>;
  /** Toggles the visibility of the filter */
  toggleShowFilter: (tableKey: string) => void;
  /** Updates search settings */
  updateSearchSettings: (
    tableKey: string,
    searchedText: string,
    searchedId: string
  ) => void;
  /** Updates the filter values */
  updateFilterValues: (tableKey: string, filters: string[]) => void;
  /** Clears all table search states */
  clearTableSearchState: () => void;
  /** Clears the table search state for entries whose keys contain the word "formTable" */
  clearFormTableSearchState: () => void;
  /** Retrieves all search settings for a given table key */
  getTableSearchState: (tableKey: string) => TableSettings;
}

const useTableSearchStore = create<TableSearchState>()(
  persist(
    (set, get) => ({
      tableSettings: {},
      toggleShowFilter: (tableKey: string) => {
        set((state) => ({
          tableSettings: {
            ...state.tableSettings,
            [tableKey]: {
              ...state.tableSettings[tableKey],
              filterToggled:
                !state.tableSettings[tableKey]?.filterToggled || false,
            },
          },
        }));
      },
      updateSearchSettings: (
        tableKey: string,
        searchedText: string,
        searchedId: string
      ) => {
        set((state) => ({
          tableSettings: {
            ...state.tableSettings,
            [tableKey]: {
              ...state.tableSettings[tableKey],
              searchedText,
              searchedId,
            },
          },
        }));
      },
      updateFilterValues: (tableKey: string, filters: string[]) => {
        set((state) => ({
          tableSettings: {
            ...state.tableSettings,
            [tableKey]: {
              ...state.tableSettings[tableKey],
              searchedFilters: filters,
            },
          },
        }));
      },
      clearFormTableSearchState: () => {
        set((state) => {
          const updatedTableSettings = Object.fromEntries(
            Object.entries(state.tableSettings).filter(
              ([key]) => !key.includes("formTable")
            )
          );

          return { tableSettings: updatedTableSettings };
        });
      },
      getTableSearchState: (tableKey: string): TableSettings => {
        // Return undefined for the values if the tableKey doesn't exist
        if (!tableKey)
          return {
            filterToggled: undefined,
            searchedText: undefined,
            searchedFilters: undefined,
            searchedId: undefined,
          };
        const tableState = get().tableSettings[tableKey];
        return {
          filterToggled: tableState?.filterToggled ?? false,
          searchedText: tableState?.searchedText ?? "",
          searchedFilters: tableState?.searchedFilters ?? [],
          searchedId: tableState?.searchedId ?? "",
        };
      },
      clearTableSearchState: () => {
        sessionStorage.removeItem(StorageKey.TABLE_SEARCH_SETTINGS);
        set({ tableSettings: {} });
      },
    }),
    {
      name: StorageKey.TABLE_SEARCH_SETTINGS,
      getStorage: () => sessionStorage,
    }
  )
);
export default useTableSearchStore;
